import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../components/icons';
import './Pilots.css';

/* ============ ICONE LOCALI (non presenti in icons.jsx) ============ */
const Svg = ({ children, size = 16, className = '', ...rest }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
        {...rest}
    >
        {children}
    </svg>
);

const IconUser = (props) => (
    <Svg {...props}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </Svg>
);

const IconTrash = (props) => (
    <Svg {...props}>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Svg>
);

const IconUpload = (props) => (
    <Svg {...props}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </Svg>
);

const IconShield = (props) => (
    <Svg {...props}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </Svg>
);

/* ============ Dropzone ============ */
function Dropzone({ label, file, onFileChange, accept = 'image/*' }) {
    const inputRef = useRef(null);
    const [isOver, setIsOver] = useState(false);

    const onDrop = (e) => {
        e.preventDefault();
        setIsOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFileChange(f);
    };

    const onClick = () => inputRef.current?.click();

    const clearFile = (e) => {
        e.stopPropagation();
        onFileChange(null);
    };

    return (
        <div
            className={`rs-dropzone pil-dropzone ${isOver ? 'is-over' : ''} ${file ? 'pil-dropzone--file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
            onDragLeave={() => setIsOver(false)}
            onDrop={onDrop}
            onClick={onClick}
            role="button"
            tabIndex={0}
        >
            {file ? (
                <div className="pil-dropzone__preview">
                    <img src={URL.createObjectURL(file)} alt="anteprima" />
                    <div className="pil-dropzone__meta">
                        <span className="pil-dropzone__name">{file.name}</span>
                        <span className="pil-dropzone__hint">Clicca per sostituire</span>
                    </div>
                    <button className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" onClick={clearFile} type="button" title="Rimuovi file" aria-label="Rimuovi file">
                        <Icon.X size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <IconUpload size={22} />
                    <strong>{label}</strong>
                    <small>Trascina o clicca per caricare</small>
                </>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFileChange(f);
                }}
            />
        </div>
    );
}

/* ============ Normalizza per ricerche ============ */
const norm = (s) =>
    (s || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');

const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT');
};

/* ============ Main Component ============ */
export default function PilotsPage({ apiBase }) {
    const [list, setList] = useState([]);
    const [teamsList, setTeamsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Tab state: 'pilot' or 'team'
    const [activeTab, setActiveTab] = useState('pilot');

    // Form state - Pilot
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [team, setTeam] = useState('');
    const [champSel, setChampSel] = useState(null);
    const [formulaSel, setFormulaSel] = useState(null);
    const [photoDriver, setPhotoDriver] = useState(null);
    const [photoTeam, setPhotoTeam] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Form state - Team
    const [teamName, setTeamName] = useState('');
    const [teamDescription, setTeamDescription] = useState('');
    const [teamLogo, setTeamLogo] = useState(null);
    const [teamChampSel, setTeamChampSel] = useState(null);
    const [teamFormulaSel, setTeamFormulaSel] = useState(null);
    const [teamDeviceId, setTeamDeviceId] = useState('');
    // Modifica inline di un team già in elenco: { id, name, deviceId, saving }
    const [editTeam, setEditTeam] = useState(null);
    const [submittingTeam, setSubmittingTeam] = useState(false);

    // Championships
    const [championships, setChampionships] = useState([]);
    const [loadingChamps, setLoadingChamps] = useState(false);

    // Search state
    const [query, setQuery] = useState('');
    const [champFilters, setChampFilters] = useState([]);

    // Delete state
    const [deletingId, setDeletingId] = useState(null);

    // Solo presentazione: pannello di creazione collassabile e conferma eliminazione inline
    const [formOpen, setFormOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'pilot' | 'team', id }

    const loadPilots = useCallback(() => {
        setLoading(true);
        setError('');
        fetch(`${apiBase}/api/pilots`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(json => setList(json))
            .catch(e => setError(e.message || 'Errore caricamento'))
            .finally(() => setLoading(false));
    }, [apiBase]);

    const loadTeams = useCallback(() => {
        fetch(`${apiBase}/api/teams`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(json => setTeamsList(json))
            .catch(() => setTeamsList([]));
    }, [apiBase]);

    const loadChamps = useCallback(() => {
        setLoadingChamps(true);
        fetch(`${apiBase}/api/championships`)
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(json => setChampionships(json))
            .catch(() => setChampionships([]))
            .finally(() => setLoadingChamps(false));
    }, [apiBase]);

    useEffect(() => {
        loadPilots();
        loadTeams();
        loadChamps();
    }, [loadPilots, loadTeams, loadChamps]);

    const onSelectChamp = (c) => {
        setChampSel(prev => (prev && prev.id === c.id) ? null : c);
        setFormulaSel(null);
    };

    const onSelectFormula = (f) => {
        setFormulaSel(prev => (prev && prev.id === f.id) ? null : f);
    };

    // Team form handlers
    const onSelectTeamChamp = (c) => {
        setTeamChampSel(prev => (prev && prev.id === c.id) ? null : c);
        setTeamFormulaSel(null);
    };

    const onSelectTeamFormula = (f) => {
        setTeamFormulaSel(prev => (prev && prev.id === f.id) ? null : f);
    };

    const toggleFilterChamp = (c) => {
        setChampFilters(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!name.trim() || !surname.trim() || !team.trim()) {
            alert('Compila Nome, Cognome e Team.');
            return;
        }
        if (!champSel) {
            alert('Seleziona un campionato.');
            return;
        }
        if (!formulaSel) {
            alert('Seleziona una formula del campionato.');
            return;
        }

        setSubmitting(true);
        const fd = new FormData();
        fd.append('name', name.trim());
        fd.append('surname', surname.trim());
        fd.append('team', team.trim());
        fd.append('championshipId', champSel.id);
        fd.append('formulaId', formulaSel.id);
        if (photoDriver) fd.append('photoDriver', photoDriver);
        if (photoTeam) fd.append('photoTeam', photoTeam);

        fetch(`${apiBase}/api/pilots`, { method: 'POST', body: fd })
            .then(r => r.ok ? r.json() : r.json().catch(() => ({})).then(j => Promise.reject(new Error(j.error || `HTTP ${r.status}`))))
            .then(() => {
                setName('');
                setSurname('');
                setTeam('');
                setChampSel(null);
                setFormulaSel(null);
                setPhotoDriver(null);
                setPhotoTeam(null);
                loadPilots();
            })
            .catch(e => alert(`Errore salvataggio: ${e.message}`))
            .finally(() => setSubmitting(false));
    };

    const onSubmitTeam = (e) => {
        e.preventDefault();
        if (!teamName.trim()) {
            alert('Inserisci il nome del team.');
            return;
        }
        if (!teamLogo) {
            alert('Il logo del team è obbligatorio (immagine quadrata).');
            return;
        }
        if (!teamChampSel) {
            alert('Seleziona un campionato.');
            return;
        }
        if (!teamFormulaSel) {
            alert('Seleziona una formula del campionato.');
            return;
        }

        setSubmittingTeam(true);
        const fd = new FormData();
        fd.append('name', teamName.trim());
        fd.append('description', teamDescription.trim());
        fd.append('championshipId', teamChampSel.id);
        fd.append('formulaId', teamFormulaSel.id);
        if (teamDeviceId.trim()) fd.append('deviceId', teamDeviceId.trim());
        fd.append('logo', teamLogo);

        fetch(`${apiBase}/api/teams`, { method: 'POST', body: fd })
            .then(r => r.ok ? r.json() : r.json().catch(() => ({})).then(j => Promise.reject(new Error(j.error || `HTTP ${r.status}`))))
            .then(() => {
                setTeamName('');
                setTeamDescription('');
                setTeamLogo(null);
                setTeamChampSel(null);
                setTeamFormulaSel(null);
                setTeamDeviceId('');
                loadTeams();
            })
            .catch(e => alert(`Errore salvataggio team: ${e.message}`))
            .finally(() => setSubmittingTeam(false));
    };

    // La conferma avviene nel riquadro .rs-confirm inline (prima: window.confirm)
    const onDelete = async (id) => {
        setDeletingId(id);
        try {
            const res = await fetch(`${apiBase}/api/pilots/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setList(prev => prev.filter(p => p.id !== id));
        } catch (e) {
            alert(`Errore eliminazione: ${e.message}`);
        } finally {
            setDeletingId(null);
        }
    };

    // Salva nome e ID dispositivo di un team esistente (PUT /api/teams/:id)
    const onSaveTeam = async () => {
        if (!editTeam) return;
        const name = String(editTeam.name || '').trim();
        if (!name) { alert('Il nome del team non può essere vuoto.'); return; }
        setEditTeam(e => ({ ...e, saving: true }));
        try {
            const fd = new FormData();
            fd.append('name', name);
            fd.append('deviceId', String(editTeam.deviceId || '').trim());
            const res = await fetch(`${apiBase}/api/teams/${editTeam.id}`, { method: 'PUT', body: fd });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
            setTeamsList(prev => prev.map(t => (t.id === j.id ? j : t)));
            setEditTeam(null);
        } catch (e) {
            alert(`Errore salvataggio team: ${e.message}`);
            setEditTeam(e2 => (e2 ? { ...e2, saving: false } : null));
        }
    };

    const onDeleteTeam = async (id) => {
        setDeletingId(id);
        try {
            const res = await fetch(`${apiBase}/api/teams/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setTeamsList(prev => prev.filter(t => t.id !== id));
        } catch (e) {
            alert(`Errore eliminazione team: ${e.message}`);
        } finally {
            setDeletingId(null);
        }
    };

    const filtered = useMemo(() => {
        const q = norm(query);
        return list.filter(p => {
            const text = `${norm(p.name)} ${norm(p.surname)} ${norm(p.team)} ${norm(p.championship?.name)} ${norm(p.formula?.label)}`;
            const textMatch = q.length === 0 || text.includes(q);
            if (champFilters.length === 0) return textMatch;
            const cId = p?.championship?.id || null;
            const has = cId && champFilters.includes(cId);
            return textMatch && !!has;
        });
    }, [list, query, champFilters]);

    const filteredTeams = useMemo(() => {
        const q = norm(query);
        return teamsList.filter(t => {
            const text = `${norm(t.name)} ${norm(t.description)} ${norm(t.championship?.name)} ${norm(t.formula?.label)}`;
            const textMatch = q.length === 0 || text.includes(q);
            if (champFilters.length === 0) return textMatch;
            const cId = t?.championship?.id || null;
            const has = cId && champFilters.includes(cId);
            return textMatch && !!has;
        });
    }, [teamsList, query, champFilters]);

    const clearFilters = () => {
        setQuery('');
        setChampFilters([]);
    };

    const selectedChampFormulas = useMemo(() => {
        if (!champSel) return [];
        const found = championships.find(c => c.id === champSel.id);
        return Array.isArray(found?.formulas) ? found.formulas : [];
    }, [champSel, championships]);

    const selectedTeamChampFormulas = useMemo(() => {
        if (!teamChampSel) return [];
        const found = championships.find(c => c.id === teamChampSel.id);
        return Array.isArray(found?.formulas) ? found.formulas : [];
    }, [teamChampSel, championships]);

    // Stats
    const teamsCount = teamsList.length;
    const formulasCount = championships.reduce((acc, c) => acc + (Array.isArray(c.formulas) ? c.formulas.length : 0), 0);
    const hasFilters = !!query || champFilters.length > 0;
    const showPilotDate = filtered.some(p => fmtDate(p.createdAt));
    const showTeamDate = filteredTeams.some(t => fmtDate(t.createdAt));

    const openForm = (tab) => {
        setActiveTab(tab);
        setFormOpen(true);
    };

    // Selettore formula: la <select> mappa sullo stesso handler toggle delle chip
    const onFormulaSelectChange = (formulas, current, handler) => (e) => {
        const f = formulas.find(x => String(x.id) === e.target.value);
        if (f) handler(f);
        else if (current) handler(current);
    };

    const renderChampChips = (selected, onSelect) => {
        if (loadingChamps) {
            return <div className="pil-form__status"><span className="rs-spinner" /> Caricamento campionati…</div>;
        }
        if (championships.length === 0) {
            return <div className="rs-alert rs-alert--warning"><Icon.Alert /> Nessun campionato disponibile: crealo dalla sezione Campionati.</div>;
        }
        return (
            <div className="pil-chips">
                {championships.map((c) => (
                    <button
                        type="button"
                        key={c.id}
                        className={`rs-btn rs-btn--sm pil-chip ${selected?.id === c.id ? 'is-active' : ''}`}
                        onClick={() => onSelect(c)}
                    >
                        {selected?.id === c.id && <Icon.Check size={13} />}
                        {c.name}
                    </button>
                ))}
            </div>
        );
    };

    const renderFormulaSelect = (champ, formulas, current, handler) => (
        <select
            className="rs-select"
            value={current ? String(current.id) : ''}
            disabled={!champ || formulas.length === 0}
            onChange={onFormulaSelectChange(formulas, current, handler)}
        >
            <option value="">
                {!champ ? 'Seleziona prima un campionato' : formulas.length === 0 ? 'Questo campionato non ha formule' : 'Seleziona formula…'}
            </option>
            {formulas.map((f) => (
                <option key={f.id} value={String(f.id)}>{f.label}</option>
            ))}
        </select>
    );

    const pilotCols = 6 + (showPilotDate ? 1 : 0);
    const teamCols = 7 + (showTeamDate ? 1 : 0);

    return (
        <div className="rs-page pil-page">
            <header className="rs-page-header">
                <div className="rs-page-header__text">
                    <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Gestione</span>
                    <h1 className="rs-page-header__title">Partecipanti</h1>
                    <p className="rs-page-header__subtitle">Anagrafica piloti e team con campionato, formula e immagini per overlay e classifica.</p>
                </div>
                <div className="rs-page-header__actions">
                    <div className="rs-search pil-search">
                        <Icon.Search />
                        <input
                            type="text"
                            className="rs-input"
                            placeholder="Cerca pilota, team, campionato…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label="Cerca piloti o team"
                        />
                    </div>
                    <button type="button" className="rs-btn rs-btn--outline" onClick={() => openForm('team')}>
                        <IconShield /> Nuovo team
                    </button>
                    <button type="button" className="rs-btn rs-btn--primary" onClick={() => openForm('pilot')}>
                        <Icon.Plus /> Nuovo pilota
                    </button>
                </div>
            </header>

            {/* KPI */}
            <section className="rs-grid rs-grid--4 pil-stats" aria-label="Riepilogo">
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{list.length}</span>
                    <span className="rs-stat__label">Piloti</span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{teamsCount}</span>
                    <span className="rs-stat__label">Team</span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{championships.length}</span>
                    <span className="rs-stat__label">Campionati</span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{formulasCount}</span>
                    <span className="rs-stat__label">Formule</span>
                </div>
            </section>

            {/* FORM CREAZIONE (collassabile) */}
            <section className={`rs-card pil-form ${formOpen ? 'is-open' : ''}`}>
                <div
                    className="rs-card__head rs-card__head--toggle"
                    onClick={() => setFormOpen(o => !o)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={formOpen}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormOpen(o => !o); } }}
                >
                    <span className="rs-card__title"><Icon.Plus /> Nuovo partecipante</span>
                    <span className="pil-form__hint rs-hide-mobile">{formOpen ? 'Compila i campi e conferma' : 'Apri per creare un pilota o un team'}</span>
                    <Icon.ChevronDown className={`rs-chevron ${formOpen ? 'rs-chevron--open' : ''}`} />
                </div>

                {formOpen && (
                    <div className="rs-card__body rs-card__body--flush">
                        <div className="rs-tabs pil-form__tabs">
                            <button
                                type="button"
                                className={`rs-tab ${activeTab === 'pilot' ? 'is-active' : ''}`}
                                onClick={() => setActiveTab('pilot')}
                            >
                                <IconUser /> Pilota
                            </button>
                            <button
                                type="button"
                                className={`rs-tab ${activeTab === 'team' ? 'is-active' : ''}`}
                                onClick={() => setActiveTab('team')}
                            >
                                <IconShield /> Team
                            </button>
                        </div>

                        {/* FORM PILOTA */}
                        {activeTab === 'pilot' && (
                            <form className="pil-form__body" onSubmit={onSubmit}>
                                <div className="pil-form__section">
                                    <div className="pil-form__section-title">Anagrafica</div>
                                    <div className="rs-grid rs-grid--3">
                                        <div className="rs-field">
                                            <label className="rs-label">Nome</label>
                                            <input
                                                type="text"
                                                className="rs-input"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Mario"
                                                required
                                            />
                                        </div>
                                        <div className="rs-field">
                                            <label className="rs-label">Cognome</label>
                                            <input
                                                type="text"
                                                className="rs-input"
                                                value={surname}
                                                onChange={(e) => setSurname(e.target.value)}
                                                placeholder="Rossi"
                                                required
                                            />
                                        </div>
                                        <div className="rs-field">
                                            <label className="rs-label">Team</label>
                                            <input
                                                type="text"
                                                className="rs-input"
                                                value={team}
                                                onChange={(e) => setTeam(e.target.value)}
                                                placeholder="Nexter Racing"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pil-form__section">
                                    <div className="pil-form__section-title">Competizione</div>
                                    <div className="rs-grid rs-grid--3 pil-form__competition">
                                        <div className="rs-field pil-field--wide">
                                            <label className="rs-label">Campionato</label>
                                            {renderChampChips(champSel, onSelectChamp)}
                                        </div>
                                        <div className="rs-field">
                                            <label className="rs-label">Formula</label>
                                            {renderFormulaSelect(champSel, selectedChampFormulas, formulaSel, onSelectFormula)}
                                        </div>
                                    </div>
                                </div>

                                <div className="pil-form__section">
                                    <div className="pil-form__section-title">Immagini <span className="rs-label__opt">opzionali</span></div>
                                    <div className="rs-grid rs-grid--2">
                                        <div className="rs-field">
                                            <label className="rs-label">Foto pilota</label>
                                            <Dropzone label="Foto pilota" file={photoDriver} onFileChange={setPhotoDriver} />
                                        </div>
                                        <div className="rs-field">
                                            <label className="rs-label">Logo team</label>
                                            <Dropzone label="Logo team" file={photoTeam} onFileChange={setPhotoTeam} />
                                        </div>
                                    </div>
                                </div>

                                <div className="pil-form__actions">
                                    <button type="button" className="rs-btn rs-btn--ghost" onClick={() => setFormOpen(false)}>Chiudi</button>
                                    <button className="rs-btn rs-btn--primary" type="submit" disabled={submitting}>
                                        {submitting ? (
                                            <><span className="rs-spinner" /> Salvataggio…</>
                                        ) : (
                                            <><Icon.Plus /> Crea pilota</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* FORM TEAM */}
                        {activeTab === 'team' && (
                            <form className="pil-form__body" onSubmit={onSubmitTeam}>
                                <div className="pil-form__section">
                                    <div className="pil-form__section-title">Team</div>
                                    <div className="rs-grid rs-grid--3">
                                        <div className="rs-field">
                                            <label className="rs-label">Nome team</label>
                                            <input
                                                type="text"
                                                className="rs-input"
                                                value={teamName}
                                                onChange={(e) => setTeamName(e.target.value)}
                                                placeholder="Scuderia Ferrari"
                                                required
                                            />
                                        </div>
                                        <div className="rs-field pil-field--wide">
                                            <label className="rs-label">Descrizione <span className="rs-label__opt">opzionale</span></label>
                                            <input
                                                type="text"
                                                className="rs-input"
                                                value={teamDescription}
                                                onChange={(e) => setTeamDescription(e.target.value)}
                                                placeholder="Descrizione del team"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pil-form__section">
                                    <div className="pil-form__section-title">Competizione</div>
                                    <div className="rs-grid rs-grid--3 pil-form__competition">
                                        <div className="rs-field pil-field--wide">
                                            <label className="rs-label">Campionato</label>
                                            {renderChampChips(teamChampSel, onSelectTeamChamp)}
                                        </div>
                                        <div className="rs-field">
                                            <label className="rs-label">Formula</label>
                                            {renderFormulaSelect(teamChampSel, selectedTeamChampFormulas, teamFormulaSel, onSelectTeamFormula)}
                                        </div>
                                        <div className="rs-field">
                                            <label className="rs-label">ID dispositivo <span className="rs-label__opt">opzionale</span></label>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className="rs-input"
                                                value={teamDeviceId}
                                                onChange={(e) => setTeamDeviceId(e.target.value)}
                                                placeholder="es. 14"
                                            />
                                            <span className="rs-hint">Numero stampato sul dispositivo: in gara viene assegnato da solo a questo team.</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pil-form__section">
                                    <div className="pil-form__section-title">Logo</div>
                                    <div className="rs-grid rs-grid--2">
                                        <div className="rs-field">
                                            <label className="rs-label">Logo team <span className="rs-label__opt">obbligatorio · immagine quadrata</span></label>
                                            <Dropzone label="Logo team (quadrato)" file={teamLogo} onFileChange={setTeamLogo} />
                                        </div>
                                    </div>
                                </div>

                                <div className="pil-form__actions">
                                    <button type="button" className="rs-btn rs-btn--ghost" onClick={() => setFormOpen(false)}>Chiudi</button>
                                    <button className="rs-btn rs-btn--primary" type="submit" disabled={submittingTeam}>
                                        {submittingTeam ? (
                                            <><span className="rs-spinner" /> Salvataggio…</>
                                        ) : (
                                            <><IconShield /> Crea team</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </section>

            {/* ELENCO */}
            <section className="rs-card pil-list">
                <div className="rs-card__head">
                    <span className="rs-card__title"><Icon.Users /> Elenco</span>
                    <div className="pil-list__counts">
                        <span className="rs-chip">Piloti <strong>{filtered.length}</strong></span>
                        <span className="rs-chip">Team <strong>{filteredTeams.length}</strong></span>
                    </div>
                </div>

                {championships.length > 0 && (
                    <div className="pil-filters">
                        <span className="rs-label">Campionato</span>
                        <div className="pil-chips">
                            {championships.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    className={`rs-btn rs-btn--sm pil-chip ${champFilters.includes(c.id) ? 'is-active' : ''}`}
                                    onClick={() => toggleFilterChamp(c)}
                                >
                                    {champFilters.includes(c.id) && <Icon.Check size={13} />}
                                    {c.name}
                                </button>
                            ))}
                        </div>
                        {hasFilters && (
                            <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm pil-filters__clear" onClick={clearFilters}>
                                <Icon.X /> Pulisci filtri
                            </button>
                        )}
                    </div>
                )}

                {loading && (
                    <div className="pil-loading"><span className="rs-spinner" /> Caricamento piloti…</div>
                )}

                {error && (
                    <div className="pil-list__msg">
                        <div className="rs-alert rs-alert--danger"><Icon.Alert /> Errore: {error}</div>
                    </div>
                )}

                {!loading && !error && filtered.length === 0 && filteredTeams.length === 0 && (list.length > 0 || teamsList.length > 0) && (
                    <div className="pil-list__msg">
                        <div className="rs-empty">
                            <Icon.Search />
                            <strong>Nessun risultato</strong>
                            <p>Nessun pilota o team corrisponde alla ricerca o ai filtri selezionati.</p>
                            <button type="button" className="rs-btn rs-btn--sm rs-btn--outline" onClick={clearFilters}>Pulisci filtri</button>
                        </div>
                    </div>
                )}

                {/* TABELLA PILOTI */}
                {!loading && !error && filtered.length > 0 && (
                    <>
                        <div className="pil-subhead">
                            <IconUser /> Piloti <span className="pil-subhead__count">{filtered.length}</span>
                        </div>
                        <div className="rs-table-wrap pil-table-wrap">
                            <table className="rs-table rs-table--dense pil-table">
                                <thead>
                                    <tr>
                                        <th className="pil-col-avatar" aria-label="Foto" />
                                        <th>Pilota</th>
                                        <th>Team</th>
                                        <th>Campionato</th>
                                        <th>Formula</th>
                                        {showPilotDate && <th>Creato</th>}
                                        <th className="pil-col-actions" aria-label="Azioni" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(p => (
                                        <React.Fragment key={p.id}>
                                            <tr className={confirmDelete?.type === 'pilot' && confirmDelete.id === p.id ? 'is-selected' : ''}>
                                                <td className="pil-col-avatar">
                                                    {p.photoDriverUrl && p.photoDriverUrl !== 'null'
                                                        ? <img className="rs-avatar rs-avatar--sm rs-avatar--round" src={`${apiBase}${p.photoDriverUrl}`} alt={`${p.name} ${p.surname}`} />
                                                        : <img className="rs-avatar rs-avatar--sm rs-avatar--round" src={`${apiBase}/uploads/pilotdefault.png`} alt="Pilota default" />}
                                                </td>
                                                <td>
                                                    <span className="pil-name">{p.name} <strong>{p.surname}</strong></span>
                                                </td>
                                                <td>
                                                    <span className="pil-team">
                                                        {p.photoTeamUrl && p.photoTeamUrl !== 'null'
                                                            ? <img className="pil-team__logo" src={`${apiBase}${p.photoTeamUrl}`} alt={p.team} />
                                                            : <img className="pil-team__logo" src={`${apiBase}/uploads/teamdefault.png`} alt="Team default" />}
                                                        <span className="rs-truncate">{p.team}</span>
                                                    </span>
                                                </td>
                                                <td>
                                                    {p.championship
                                                        ? <span className="rs-badge">{p.championship.name}</span>
                                                        : <span className="rs-text-faint">—</span>}
                                                </td>
                                                <td>
                                                    {p.formula
                                                        ? <span className="rs-badge rs-badge--red">{p.formula.label}</span>
                                                        : <span className="rs-text-faint">—</span>}
                                                </td>
                                                {showPilotDate && <td><span className="pil-date">{fmtDate(p.createdAt) || '—'}</span></td>}
                                                <td className="pil-col-actions">
                                                    <button
                                                        type="button"
                                                        className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon pil-delete"
                                                        title="Elimina pilota"
                                                        aria-label="Elimina pilota"
                                                        onClick={() => setConfirmDelete({ type: 'pilot', id: p.id })}
                                                        disabled={deletingId === p.id}
                                                    >
                                                        {deletingId === p.id ? <span className="rs-spinner" /> : <IconTrash />}
                                                    </button>
                                                </td>
                                            </tr>
                                            {confirmDelete?.type === 'pilot' && confirmDelete.id === p.id && (
                                                <tr className="pil-confirm-row">
                                                    <td colSpan={pilotCols}>
                                                        <div className="rs-confirm pil-confirm">
                                                            <div className="rs-confirm__text">
                                                                Eliminare il pilota <strong>{p.name} {p.surname}</strong>? L'operazione non è reversibile.
                                                            </div>
                                                            <div className="rs-confirm__actions">
                                                                <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => setConfirmDelete(null)}>Annulla</button>
                                                                <button
                                                                    type="button"
                                                                    className="rs-btn rs-btn--sm rs-btn--danger"
                                                                    disabled={deletingId === p.id}
                                                                    onClick={() => { setConfirmDelete(null); onDelete(p.id); }}
                                                                >
                                                                    <IconTrash /> Elimina
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* TABELLA TEAM */}
                {!loading && !error && filteredTeams.length > 0 && (
                    <>
                        <div className="pil-subhead pil-subhead--team">
                            <IconShield /> Team <span className="pil-subhead__count">{filteredTeams.length}</span>
                        </div>
                        <div className="rs-table-wrap pil-table-wrap">
                            <table className="rs-table rs-table--dense pil-table">
                                <thead>
                                    <tr>
                                        <th className="pil-col-avatar" aria-label="Logo" />
                                        <th>Team</th>
                                        <th>Descrizione</th>
                                        <th>Campionato</th>
                                        <th>Formula</th>
                                        <th className="pil-col-device">Dispositivo</th>
                                        {showTeamDate && <th>Creato</th>}
                                        <th className="pil-col-actions" aria-label="Azioni" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTeams.map(t => (
                                        <React.Fragment key={t.id}>
                                            <tr className={confirmDelete?.type === 'team' && confirmDelete.id === t.id ? 'is-selected' : ''}>
                                                <td className="pil-col-avatar">
                                                    {t.logoUrl && t.logoUrl !== 'null'
                                                        ? <img className="rs-avatar rs-avatar--sm pil-avatar--logo" src={`${apiBase}${t.logoUrl}`} alt={t.name} />
                                                        : <img className="rs-avatar rs-avatar--sm pil-avatar--logo" src={`${apiBase}/uploads/teamdefault.png`} alt="Team default" />}
                                                </td>
                                                <td><span className="pil-name"><strong>{t.name}</strong></span></td>
                                                <td>
                                                    {t.description
                                                        ? <span className="pil-desc" title={t.description}>{t.description}</span>
                                                        : <span className="rs-text-faint">—</span>}
                                                </td>
                                                <td>
                                                    {t.championship
                                                        ? <span className="rs-badge">{t.championship.name}</span>
                                                        : <span className="rs-text-faint">—</span>}
                                                </td>
                                                <td>
                                                    {t.formula
                                                        ? <span className="rs-badge rs-badge--red">{t.formula.label}</span>
                                                        : <span className="rs-text-faint">—</span>}
                                                </td>
                                                <td className="pil-col-device">
                                                    {t.deviceId
                                                        ? <span className="rs-chip pil-device" title="Numero del dispositivo assegnato"><Icon.Cpu size={12} />{t.deviceId}</span>
                                                        : <span className="rs-text-faint">—</span>}
                                                </td>
                                                {showTeamDate && <td><span className="pil-date">{fmtDate(t.createdAt) || '—'}</span></td>}
                                                <td className="pil-col-actions">
                                                    <button
                                                        type="button"
                                                        className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon"
                                                        title="Modifica nome e dispositivo"
                                                        aria-label="Modifica team"
                                                        onClick={() => setEditTeam({ id: t.id, name: t.name, deviceId: t.deviceId || '' })}
                                                    >
                                                        <Icon.Settings size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon pil-delete"
                                                        title="Elimina team"
                                                        aria-label="Elimina team"
                                                        onClick={() => setConfirmDelete({ type: 'team', id: t.id })}
                                                        disabled={deletingId === t.id}
                                                    >
                                                        {deletingId === t.id ? <span className="rs-spinner" /> : <IconTrash />}
                                                    </button>
                                                </td>
                                            </tr>
                                            {editTeam?.id === t.id && (
                                                <tr className="pil-confirm-row">
                                                    <td colSpan={teamCols}>
                                                        <div className="rs-confirm pil-edit">
                                                            <div className="pil-edit__fields">
                                                                <div className="rs-field">
                                                                    <label className="rs-label">Nome team</label>
                                                                    <input
                                                                        type="text"
                                                                        className="rs-input"
                                                                        value={editTeam.name}
                                                                        onChange={(e) => setEditTeam(s2 => ({ ...s2, name: e.target.value }))}
                                                                    />
                                                                </div>
                                                                <div className="rs-field pil-edit__device">
                                                                    <label className="rs-label">ID dispositivo</label>
                                                                    <input
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        className="rs-input"
                                                                        value={editTeam.deviceId}
                                                                        onChange={(e) => setEditTeam(s2 => ({ ...s2, deviceId: e.target.value }))}
                                                                        placeholder="vuoto = nessuno"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="rs-confirm__actions">
                                                                <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => setEditTeam(null)}>Annulla</button>
                                                                <button type="button" className="rs-btn rs-btn--sm rs-btn--primary" disabled={editTeam.saving} onClick={onSaveTeam}>
                                                                    {editTeam.saving ? <><span className="rs-spinner" /> Salvataggio…</> : <><Icon.Check size={14} /> Salva</>}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {confirmDelete?.type === 'team' && confirmDelete.id === t.id && (
                                                <tr className="pil-confirm-row">
                                                    <td colSpan={teamCols}>
                                                        <div className="rs-confirm pil-confirm">
                                                            <div className="rs-confirm__text">
                                                                Eliminare il team <strong>{t.name}</strong>? L'operazione non è reversibile.
                                                            </div>
                                                            <div className="rs-confirm__actions">
                                                                <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => setConfirmDelete(null)}>Annulla</button>
                                                                <button
                                                                    type="button"
                                                                    className="rs-btn rs-btn--sm rs-btn--danger"
                                                                    disabled={deletingId === t.id}
                                                                    onClick={() => { setConfirmDelete(null); onDeleteTeam(t.id); }}
                                                                >
                                                                    <IconTrash /> Elimina
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {!loading && !error && filtered.length === 0 && filteredTeams.length === 0 && list.length === 0 && teamsList.length === 0 && (
                    <div className="pil-list__msg">
                        <div className="rs-empty">
                            <Icon.Users />
                            <strong>Nessun pilota o team</strong>
                            <p>Crea il primo partecipante: servono un campionato e una formula già definiti.</p>
                            <button type="button" className="rs-btn rs-btn--sm rs-btn--primary" onClick={() => openForm('pilot')}><Icon.Plus /> Nuovo pilota</button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
